import { useState, useEffect, useRef, useSyncExternalStore } from 'react';
import Avatar from './Avatar';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import { useApp } from '../context/useApp';
import { LogoIconSmall } from '../context/TenantContext';
import { useTenant } from '../context/useTenant';
import { openDocumentLibraryWindow, openMapWindow } from '../utils/openDocumentInTab';
import { usePresence } from '../context/usePresence';
import { playHoverTick, getSoundMuted, setSoundMuted } from '../utils/uiSound';
import { BUYER_MAP_PATH, canAccessBuyers, canAccessInvestors, canAccessPrivateCrm, isBuyerOnlyUser, isOutsiderUser } from '../utils/accessControl';
import { userOnboardingPending } from '../utils/onboardingStatus';
import useInvestorView from '../hooks/useInvestorView';
import { useIsMobile } from '../hooks/useIsMobile';
import NotificationCenter from './NotificationCenter';
import AppImprovementModal from './AppImprovementModal';
import TitleRequestModal from './TitleRequestModal';
import MyMailMenu from './MyMailMenu';
import { subscribeOverlays, topOverlayZIndex, improvementLayer } from '../utils/modalLayer';
import {
  BriefcaseBusiness, Library, LogOut, MapPin, Settings, LayoutDashboard, ChevronDown, Users,
  AlertTriangle, X, Volume2, VolumeX,
  GraduationCap, UserPlus, Activity, Lightbulb, ScrollText, Printer, Home, Landmark, Eye, EyeOff, FileSignature,
} from 'lucide-react';

// What to call the page a change request came from. The screenshot shows
// what it looked like; this says where it was, in the words the person
// raising it would use rather than a route path.
const PAGE_LABELS = [
  [/^\/deals/, 'Deal Tracker'],
  [/^\/deal\//, 'Deal'],
  [/^\/crm/, 'Lead Center'],
  [/^\/map/, 'Map'],
  [/^\/documents/, 'Documents'],
  [/^\/buyers/, 'Buyers'],
  [/^\/investors/, 'Investors'],
  [/^\/admin|^\/settings/, 'Settings'],
  [/^\/accounting/, 'Accounting'],
  [/^\/onboarding/, 'Onboarding'],
  [/^\/tenant-home/, 'Home'],
  [/^\/home/, 'Home'],
];

function pageLabelFor(pathname) {
  const hit = PAGE_LABELS.find(([pattern]) => pattern.test(pathname));
  return hit ? hit[1] : pathname.replace(/^\//, '') || 'App';
}

// Every page renders inside a shared layout with a sticky top header
// bar. The header hosts:
//  - tenant logo on the left
//  - profile chip + dropdown on the right (primary nav, map shortcut,
//    admin settings, logout)
//
// The header lives INSIDE .main-content so the main-content block is
// still the full-viewport scroll container the OwnerDashboard's
// GridLayout resizes against — no width-measurement regressions.
// The workspace menu, in order. Home first: it is the page signing in
// lands on and the one carrying what changed since you last looked, so
// it is where "go back to the start" means. Lead Center next — the
// first stop of the working day once you have read it.
//
// `when` gates an item; Lead Center, Buyers and Investors each have one.
const navItems = [
  { to: '/home', icon: Home, label: 'Home' },
  { to: '/crm', icon: UserPlus, label: 'Lead Center', when: canAccessPrivateCrm },
  { to: '/deals', icon: LayoutDashboard, label: 'Deal Tracker' },
  { to: '/documents', icon: Library, label: 'Documents' },
  // Staff only, like the rest: the queue lists every seller's name and
  // home address, which is most of what it is for and none of a buyer's
  // business.
  { to: '/mailroom', icon: Printer, label: 'Mailroom', when: (user) => !isOutsiderUser(user) },
  { to: '/buyers', icon: Users, label: 'Buyers', when: canAccessBuyers },
  { to: '/investors', icon: Landmark, label: 'Investors', when: canAccessInvestors },
];
export default function Layout() {
  const { user, logout } = useAuth();
  const tenant = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  // "Improve this" lives in the shell rather than on one page, so a
  // change request can be raised from wherever the problem actually is
  // — and the screenshot it takes is of that page, not of the one
  // screen that happened to carry the button.
  const [improvementOpen, setImprovementOpen] = useState(false);
  // "Request title" sits beside it, and for the same reason: an agent
  // spots the land they want run while they are looking at something
  // else, and the shell is the only place that is true on every page.
  const [titleRequestOpen, setTitleRequestOpen] = useState(false);
  // Which layer "Improve this" needs to occupy. The header button sits
  // at z-index 100, so any open dialog covered it — the button went
  // dead exactly when somebody had a form in front of them to report
  // about. ModalOverlay reports what is open; this puts the button one
  // rung above it and the report modal one above that.
  // useSyncExternalStore rather than subscribe-into-setState: this is an
  // external store, and reading it through an effect makes React render
  // once with a stale value and again with the real one on every open.
  const overlayTopZ = useSyncExternalStore(subscribeOverlays, topOverlayZIndex, () => 0);
  const improveLayer = improvementLayer(overlayTopZ);
  const { storageError, clearStorageError } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  // On phones we hide the presence stack and shrink the profile chip
  // so the right side of the topbar stops overflowing on a 375px
  // viewport. Desktop behaviour is unchanged.
  const isMobile = useIsMobile();
  const buyerOnly = isBuyerOnlyUser(user);
  // The app's own chrome — who is online, "Improve this page", Settings —
  // is for the people who work here. Gated on the concept rather than on
  // buyerOnly, so the next kind of outsider inherits it by existing.
  const outsider = isOutsiderUser(user);
  const [investorViewOn, setInvestorViewOn] = useInvestorView();
  const visibleNavItems = navItems.filter((item) => !item.when || item.when(user));
  const tenantNavItems = tenant?.id === 'oklahoma'
    ? [{ to: '/tenant-home', icon: Activity, label: 'Oklahoma Hub' }, ...visibleNavItems]
    : visibleNavItems;

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (buyerOnly && location.pathname !== BUYER_MAP_PATH) {
      navigate(BUYER_MAP_PATH, { replace: true });
    }
  }, [buyerOnly, location.pathname, navigate]);

  // Global hover-tick wiring. One delegated listener on document so
  // every page gets the same audio cue without each one re-attaching.
  // Fires on:
  //   button.dashcard-handle          dashboard button-tile cards
  //   .deal-card                      kanban cards on the Deal Tracker
  //   .pipeline-kpi                   the Active/Pipeline/Profit tiles
  //   [data-hover-tick]               opt-in marker for any other surface
  // Tracks the last hovered element so the tick only fires when the
  // pointer enters a NEW target, not on every mousemove inside it.
  useEffect(() => {
    let lastTarget = null;
    const tickSelector = [
      'button.dashcard-handle',     // dashboard button-tile cards
      '.deal-card',                 // kanban cards on the Deal Tracker
      '.pipeline-kpi',              // Active / Pipeline / Profit tiles
      '.layout-menu button',        // dropdown menu items (Deals / Map / Buyers / Closings / Notification Settings / etc.)
      '.layout-menu a',             // NavLink items inside the dropdown
      '[data-hover-tick]',          // explicit opt-in for any other surface
    ].join(', ');
    const onOver = (e) => {
      const target = e.target.closest && e.target.closest(tickSelector);
      if (!target || target === lastTarget) return;
      if (target.disabled) { lastTarget = target; return; }
      lastTarget = target;
      playHoverTick();
    };
    const onOut = (e) => {
      // Reset when the cursor leaves the document so the same target
      // ticks again on re-entry.
      if (!e.relatedTarget) lastTarget = null;
    };
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openMap = () => {
    openMapWindow();
  };
  return (
    <div className="app-layout">
      <main className="main-content">
        {/* Mounted in the shell so every page can be reported from. */}
        <TitleRequestModal
          open={titleRequestOpen}
          onClose={() => setTitleRequestOpen(false)}
          zIndex={improveLayer.modal}
        />
        <AppImprovementModal
          open={improvementOpen}
          onClose={() => setImprovementOpen(false)}
          pageLabel={pageLabelFor(location.pathname)}
          zIndex={improveLayer.modal}
        />

        {/* While a dialog is open the header — and the button in it — is
            underneath the backdrop, so the same control is offered as a
            floating one above the dialog. Same handler, same reporting
            path; the screenshot then captures the form being reported
            on rather than the page behind it. */}
        {!outsider && improveLayer.floating && !improvementOpen && (
          <button
            type="button"
            data-app-improvement-open-floating
            onClick={() => setImprovementOpen(true)}
            title="Suggest a change to what is open"
            aria-label="Suggest a change to what is open"
            style={{
              position: 'fixed',
              top: 10,
              right: 14,
              zIndex: improveLayer.button,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 28,
              padding: '0 10px',
              background: 'rgba(0, 29, 74, 0.96)',
              color: '#f1f5f9',
              border: '1px solid rgba(91, 191, 239, 0.55)',
              borderRadius: 0,
              cursor: 'pointer',
              fontSize: '0.74rem',
              fontWeight: 500,
              boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
            }}
          >
            <Lightbulb size={15} />
            Improve
          </button>
        )}
        {/* Sticky top header bar. Sits flush with the top of the
            viewport and stays pinned while the content below scrolls.
            Lives inside .main-content so the scroll container's
            dimensions are unchanged. */}
        <header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            // Three-column grid so the logo + wordmark sit dead center
            // regardless of the profile chip's variable width.
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.45rem 0.9rem',
            background: 'rgba(0, 29, 74, 0.96)',
            color: '#f1f5f9',
            borderBottom: '1px solid rgba(91, 191, 239, 0.35)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            backdropFilter: 'blur(4px)',
          }}
        >
          {/* Left header actions are populated by route-level portals. */}
          <div
            id="layout-top-header-actions"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifySelf: 'start',
              minWidth: 0,
            }}
          >
            {/* Investor View, in the top-left corner of every page.
                It lives here rather than on the Deal Tracker because
                what it hides is on more than one screen — the board's
                margins and the Owner Dashboard's buy and margin
                columns — and a switch that is only reachable from one
                of them is a switch somebody turns on and then cannot
                find to turn off.

                Loud when it is on. The failure that matters is not an
                investor glimpsing a margin; it is reading the board
                yourself, hours later, as though the deals had no spread
                on them. */}
            {!outsider && (
              <button
                type="button"
                data-hover-tick
                data-investor-view-toggle={investorViewOn ? 'on' : 'off'}
                aria-pressed={investorViewOn}
                onClick={() => setInvestorViewOn(!investorViewOn)}
                title={investorViewOn
                  ? 'Investor View is ON — margins and buy prices are hidden everywhere. Click to show them again.'
                  : 'Hide margins and buy prices so the screen can be shown to an investor'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  height: 28, padding: '0 10px',
                  background: investorViewOn ? '#f59e0b' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${investorViewOn ? '#fbbf24' : 'rgba(91,191,239,0.4)'}`,
                  borderRadius: 0,
                  color: investorViewOn ? '#451a03' : '#cfe0f5',
                  fontSize: '0.74rem',
                  fontWeight: investorViewOn ? 700 : 500,
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                {investorViewOn ? <EyeOff size={14} /> : <Eye size={14} />}
                Investor View
              </button>
            )}
          </div>

          {/* Center: tenant logo + wordmark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifySelf: 'center' }}>
            <LogoIconSmall size={22} />
            <span
              style={{
                fontSize: '0.82rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                color: '#f1f5f9',
                textTransform: 'uppercase',
              }}
            >
              {tenant?.name || 'Paraclete'}
            </span>
          </div>

          {/* Right: presence stack + profile chip + dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 10, justifySelf: 'end' }}>
            {!outsider && !isMobile && <PresenceStack />}
            {/* Mail you asked for. It sat on the Deal Tracker, which is
                where it gets requested — but it is wanted wherever you
                happen to be when you wonder whether it went, and that is
                the same argument that put Improve and Title here. It
                still hides itself for anybody who has never asked for
                anything, which is most people. */}
            {!outsider && <MyMailMenu compact={isMobile} />}
            {!outsider && (
              <button
                type="button"
                data-app-improvement-open
                onClick={() => setImprovementOpen(true)}
                title="Suggest a change to this page"
                aria-label="Suggest a change to this page"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  height: 28,
                  padding: isMobile ? '0 7px' : '0 10px',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#f1f5f9',
                  border: '1px solid rgba(91, 191, 239, 0.35)',
                  borderRadius: 0,
                  cursor: 'pointer',
                  fontSize: '0.74rem',
                  fontWeight: 500,
                }}
              >
                <Lightbulb size={15} />
                {!isMobile && 'Improve'}
              </button>
            )}
            {/* Beside Improve, and styled as its sibling: the two are the
                same kind of thing — something you noticed, said from
                wherever you noticed it. This one asks for title to be run
                on land rather than for the app to change, and it goes to
                Mays to approve because approving it books billable work in
                another company. */}
            {!outsider && (
              <button
                type="button"
                data-title-request-open
                onClick={() => setTitleRequestOpen(true)}
                title="Ask for title to be run on some land"
                aria-label="Ask for title to be run on some land"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  height: 28,
                  padding: isMobile ? '0 7px' : '0 10px',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#f1f5f9',
                  border: '1px solid rgba(91, 191, 239, 0.35)',
                  borderRadius: 0,
                  cursor: 'pointer',
                  fontSize: '0.74rem',
                  fontWeight: 500,
                }}
              >
                <ScrollText size={15} />
                {!isMobile && 'Title'}
              </button>
            )}
            {!outsider && !isMobile && <SoundToggle />}
            {!outsider && <NotificationCenter />}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-layout-menu-trigger
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '0.28rem 0.6rem 0.28rem 0.32rem',
                background: menuOpen ? 'rgba(91,191,239,0.18)' : 'rgba(255,255,255,0.06)',
                color: '#f1f5f9',
                border: `1px solid ${menuOpen ? 'rgba(91,191,239,0.75)' : 'rgba(91, 191, 239, 0.35)'}`,
                borderRadius: 0,
                cursor: 'pointer',
                fontSize: '0.78rem',
                fontWeight: 500,
                transition: 'background 0.15s ease, border-color 0.15s ease',
              }}
            >
              <AvatarDisk
                name={user?.name || user?.email}
                avatarUrl={user?.avatarUrl}
                color="#5BBFEF"
                size={22}
                border="none"
                square
              />
              {!isMobile && (
                <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.name || 'Account'}
                </span>
              )}
              <ChevronDown size={13} style={{
                opacity: 0.75,
                transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s',
              }} />
            </button>

            {menuOpen && (
              <div
                className="layout-menu"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 6px)',
                  background: '#ffffff',
                  border: '1px solid #d9e2ec',
                  borderTop: '3px solid #5BBFEF',
                  borderRadius: 0,
                  boxShadow: '0 2px 6px rgba(2,6,23,0.12), 0 24px 60px rgba(2,6,23,0.32)',
                  minWidth: 252,
                  overflow: 'hidden',
                  animation: 'layoutMenuIn 0.16s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                <style>{`
                  @keyframes layoutMenuIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
                  .layout-menu a, .layout-menu button { transition: background 0.12s ease, padding-left 0.12s ease; }
                  .layout-menu a:hover, .layout-menu button:hover { background: #eff6ff !important; padding-left: 1.05rem !important; }
                  .layout-menu [data-menu-logout]:hover { background: #fef2f2 !important; }
                  .layout-menu a svg, .layout-menu button svg { color: #1d4ed8; }
                  .layout-menu [data-menu-logout] svg { color: #b91c1c; }
                `}</style>
                <div style={{
                  padding: '0.85rem 0.95rem',
                  background: 'linear-gradient(135deg, #0c1e3f 0%, #003279 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                }}>
                  <AvatarDisk
                    name={user?.name || user?.email}
                    avatarUrl={user?.avatarUrl}
                    color="#5BBFEF"
                    size={36}
                    border="none"
                    square
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.name || 'Account'}
                    </div>
                    <div style={{ fontSize: '0.66rem', color: '#a5c4f1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
                    {user?.role && (
                      <span style={{
                        display: 'inline-block', marginTop: 4, padding: '0.1rem 0.48rem',
                        background: 'rgba(91,191,239,0.2)', border: '1px solid rgba(91,191,239,0.45)',
                        borderRadius: 0,
                        color: '#bfe3f8', fontSize: '0.56rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.6px',
                      }}>
                        {user.role}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ padding: '0.5rem 0.95rem 0.1rem', color: '#94a3b8', fontSize: '0.56rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.75px' }}>
                  Workspace
                </div>

                {/* Primary nav lives in the dropdown now. Order and
                    gating both come from navItems, so there is one
                    place that decides what a person sees and where. */}
                <div style={{ padding: '0.25rem 0' }}>
                  {!outsider && tenantNavItems.map((item) => {
                    const Icon = item.icon;
                    // Documents opens in a half-screen new window
                    // (right edge of the screen) so the user can keep
                    // the dashboard / map visible on the left.
                    if (item.to === '/documents') {
                      return (
                        <button
                          key={item.to}
                          onClick={() => { openDocumentLibraryWindow(); setMenuOpen(false); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                            padding: '0.5rem 0.9rem',
                            background: 'transparent', border: 'none',
                            color: '#0f172a', fontSize: '0.82rem', fontWeight: 500,
                            cursor: 'pointer', textAlign: 'left',
                          }}
                          title="Opens in a new window pinned to the right half of the screen"
                        >
                          <Icon size={15} /> {item.label}
                        </button>
                      );
                    }
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setMenuOpen(false)}
                        style={({ isActive }) => ({
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '0.5rem 0.9rem',
                          color: isActive ? '#1e3a8a' : '#0f172a',
                          background: isActive ? '#eff6ff' : 'transparent',
                          textDecoration: 'none',
                          fontSize: '0.82rem',
                          fontWeight: 500,
                        })}
                      >
                        <Icon size={15} /> {item.label}
                      </NavLink>
                    );
                  })}
                  {!outsider && (
                  <button
                    onClick={() => { openMap(); setMenuOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '0.5rem 0.9rem',
                      background: 'transparent', border: 'none',
                      color: '#0f172a', fontSize: '0.82rem', fontWeight: 500,
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <MapPin size={15} /> Map
                  </button>
                  )}
                  {!outsider && user?.role === 'admin' && (
                    <button
                      onClick={() => { navigate('/assets-under-management'); setMenuOpen(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '0.5rem 0.9rem',
                        background: 'transparent', border: 'none',
                        color: '#0f172a', fontSize: '0.82rem', fontWeight: 500,
                        cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <BriefcaseBusiness size={15} /> Assets Under Management
                    </button>
                  )}
                </div>

                <div style={{ borderTop: '1px solid #e2e8f0', padding: '0.1rem 0 0.25rem' }}>
                  <div style={{ padding: '0.45rem 0.95rem 0.1rem', color: '#94a3b8', fontSize: '0.56rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.75px' }}>
                    Account
                  </div>
                  {/* Single "Settings" entry — Team / Notifications /
                      Accounting is a tab inside the page now. */}
                  {!outsider && (
                  <button
                    onClick={() => { navigate('/admin'); setMenuOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '0.5rem 0.9rem',
                      background: 'transparent', border: 'none',
                      color: '#0f172a', fontSize: '0.82rem', fontWeight: 500,
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <Settings size={15} /> Settings
                  </button>
                  )}
                  <button
                    data-menu-logout
                    onClick={handleLogout}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '0.5rem 0.9rem',
                      background: 'transparent', border: 'none',
                      color: '#b91c1c', fontSize: '0.82rem', fontWeight: 600,
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <LogOut size={15} /> Logout
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        </header>

        {/* Onboarding used to be a wall across every route. It is this
            line instead: visible on every page until the packet is in,
            gone the moment it is, and never in the way of the work. */}
        {userOnboardingPending(user) && !location.pathname.startsWith('/onboarding') && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '9px 14px', margin: '8px 12px 0',
              background: '#fffbeb', border: '1px solid #fde68a',
              borderLeft: '4px solid #f59e0b', borderRadius: 6,
              color: '#78350f', fontSize: '0.83rem',
            }}
          >
            <FileSignature size={16} style={{ color: '#b45309', flexShrink: 0 }} />
            <span style={{ flex: 1, lineHeight: 1.4 }}>
              Your new-hire paperwork isn&rsquo;t finished — W-9, direct deposit and your contractor
              agreement. Accounting needs it on file before your first commission can be paid.
            </span>
            <button
              type="button"
              onClick={() => navigate('/onboarding')}
              style={{
                background: '#b45309', border: 'none', color: '#fff', borderRadius: 5,
                padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Finish it
            </button>
          </div>
        )}

        {storageError && (
          <div
            role="alert"
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '10px 14px', margin: '8px 12px 0',
              background: '#fef2f2', border: '1px solid #fecaca',
              borderLeft: '4px solid #dc2626', borderRadius: 6,
              color: '#7f1d1d', fontSize: '0.85rem',
            }}
          >
            <AlertTriangle size={16} style={{ color: '#dc2626', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, lineHeight: 1.4 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{storageError.title}</div>
              <div>{storageError.message}</div>
            </div>
            <button
              type="button"
              onClick={clearStorageError}
              title="Dismiss"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#7f1d1d', padding: 2, flexShrink: 0,
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}

        <Outlet />
      </main>
      {isMobile && !outsider && (
            <MobileBottomNav
              pathname={location.pathname}
              openMap={openMap}
              openDocuments={() => openDocumentLibraryWindow()}
              tenantId={tenant?.id}
              showBuyers={canAccessBuyers(user)}
            />
      )}
    </div>
  );
}

function MobileBottomNav({ pathname, openMap, openDocuments, tenantId, showBuyers }) {
  const items = [
    ...(tenantId === 'oklahoma'
      ? [{ kind: 'link', to: '/tenant-home', icon: Activity, label: 'Hub', active: pathname === '/tenant-home' }]
      : []),
    { kind: 'link', to: '/home', icon: Home, label: 'Home', active: pathname === '/home' },
    { kind: 'link', to: '/deals', icon: LayoutDashboard, label: 'Deals', active: pathname === '/deals' || pathname.startsWith('/owners/') },
    { kind: 'button', icon: MapPin, label: 'Map', onClick: openMap, active: pathname === '/map' || pathname === '/map-only' },
    { kind: 'button', icon: Library, label: 'Docs', onClick: openDocuments, active: pathname === '/documents' },
    ...(showBuyers
      ? [{ kind: 'link', to: '/buyers', icon: Users, label: 'Buyers', active: pathname === '/buyers' }]
      : []),
  ];

  return (
    <nav className="mobile-bottom-nav" aria-label="Primary mobile navigation">
      {items.map((item) => {
        const Icon = item.icon;
        const content = (
          <>
            <Icon size={19} strokeWidth={2.35} />
            <span>{item.label}</span>
          </>
        );
        if (item.kind === 'button') {
          return (
            <button
              key={item.label}
              type="button"
              className={`mobile-bottom-nav-item${item.active ? ' active' : ''}`}
              onClick={item.onClick}
              aria-label={item.label}
              aria-current={item.active ? 'page' : undefined}
              data-hover-tick
            >
              {content}
            </button>
          );
        }
        return (
          <NavLink
            key={item.to}
            to={item.to}
            className={`mobile-bottom-nav-item${item.active ? ' active' : ''}`}
            aria-label={item.label}
            aria-current={item.active ? 'page' : undefined}
            data-hover-tick
          >
            {content}
          </NavLink>
        );
      })}
    </nav>
  );
}

// Header badge showing every OTHER user currently in the app. Each
// peer is a small colored disk with their initials; hover the stack
// and a dropdown panel lists full names + what they're looking at
// right now. Empty when nobody else is online — no visual noise.

function PresenceStack() {
  const { peers } = usePresence();
  if (!peers || peers.length === 0) return null;
  const visible = peers.slice(0, 4);
  const extra = peers.length - visible.length;
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex', alignItems: 'center',
        paddingRight: extra > 0 ? 0 : 4,
      }}
      className="presence-stack-wrap"
    >
      {visible.map((p, i) => (
        <AvatarDisk
          key={p.id}
          name={p.name}
          avatarUrl={p.avatarUrl}
          color={p.color}
          size={26}
          border="2px solid #001d4a"
          title={`${p.name} · ${p.page}`}
          style={{
            marginLeft: i === 0 ? 0 : -8,
            zIndex: visible.length - i,
          }}
        />
      ))}
      {extra > 0 && (
        <div
          title={peers.slice(4).map((p) => `${p.name} · ${p.page}`).join('\n')}
          style={{
            width: 26, height: 26,
            borderRadius: '50%',
            background: '#334155', color: '#f1f5f9',
            fontSize: '0.64rem', fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #001d4a',
            marginLeft: -8,
          }}
        >
          +{extra}
        </div>
      )}
      {/* Hover panel with full names + pages */}
      <div
        className="presence-stack-panel"
        style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          minWidth: 220, zIndex: 500,
          background: '#ffffff', color: '#0f172a',
          border: '1px solid #cbd5e1',
          boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
          padding: '0.45rem',
          opacity: 0, pointerEvents: 'none',
          transition: 'opacity 0.12s ease',
        }}
      >
        <div style={{
          fontSize: '0.6rem', fontWeight: 600, color: '#64748b',
          textTransform: 'uppercase', letterSpacing: '0.5px',
          padding: '0.15rem 0.35rem',
        }}>
          Online now ({peers.length})
        </div>
        {peers.map((p) => (
          <div
            key={p.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '0.3rem 0.4rem',
              borderTop: '1px solid #f1f5f9',
            }}
          >
            <AvatarDisk
              name={p.name}
              avatarUrl={p.avatarUrl}
              color={p.color}
              size={18}
              border="1px solid rgba(15,23,42,0.08)"
              style={{ flexShrink: 0, boxShadow: '0 0 0 2px rgba(0,0,0,0.08)' }}
            />
            <span style={{ flex: 1, minWidth: 0, fontSize: '0.78rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.name}
            </span>
            <span style={{ fontSize: '0.68rem', color: '#64748b', whiteSpace: 'nowrap' }}>
              {p.page}
            </span>
          </div>
        ))}
      </div>
      <style>{`
        .presence-stack-wrap:hover .presence-stack-panel,
        .presence-stack-wrap:focus-within .presence-stack-panel {
          opacity: 1;
          pointer-events: auto;
        }
      `}</style>
    </div>
  );
}

// Layout's own avatar used to live here: its own initials rule (first
// letter of the first two words) and one #5BBFEF disk for everybody.
// The deal chat had a second implementation that disagreed on both. One
// component now, in Avatar.jsx, which also falls back to initials when
// a Google photo URL 403s — these rotate, and this bar is where a
// broken-image glyph would have shown up first.
function AvatarDisk(props) {
  return <Avatar {...props} />;
}

// Sound toggle for the hover click. Sits in the topbar next to the
// notification bell. Persists muted state to localStorage so closing
// + reopening the tab respects the user's choice. The actual mute
// check happens inside playHoverTick (uiSound.js) — this component
// just flips the flag and re-renders its own icon.
function SoundToggle() {
  const [muted, setMuted] = useState(() => getSoundMuted());
  // Pick up cross-tab toggles via the storage event so a setting
  // change in tab A reflects in tab B without a reload.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'shadowland-sound-muted') setMuted(getSoundMuted());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const toggle = () => {
    const next = !muted;
    setSoundMuted(next);
    setMuted(next);
  };
  const Icon = muted ? VolumeX : Volume2;
  return (
    <button
      type="button"
      onClick={toggle}
      title={muted ? 'Click sound is muted — click to unmute' : 'Click sound is on — click to mute'}
      aria-label={muted ? 'Unmute click sound' : 'Mute click sound'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32,
        background: 'transparent',
        border: 'none',
        color: muted ? '#94a3b8' : '#e2e8f0',
        cursor: 'pointer',
        borderRadius: 0,
        transition: 'color 0.15s ease, background 0.15s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon size={16} />
    </button>
  );
}
